<template>
	<div class="mt-base">
		<div class="g-firebase pl-24" style="padding-right: 30.7%">
			<div v-if="!twoFactorAuth || !showInputCode">
				<div class="vx-row">
					<div class="vx-col w-full" v-show="!showInputPassword">
						<label v-text="$t('general.Email')"></label>
						<vs-input
							v-validate="'required|email|min:3'"
							data-vv-validate-on="blur"
							name="email"
							placeholder="Ej: rutify@info.com"
							v-model="email"
							class="w-full"
						/>
						<span class="text-danger text-sm" v-show="errors.has('email')">{{
							$t("Login.ValidMessages.Email")
						}}</span>
					</div>
				</div>
				<div class="vx-row" v-show="showInputPassword">
					<div class="vx-col w-full">
						<label v-text="$t('Login.Password')"></label>
						<span class="span-description" v-text="'(' + $t('Login.ValidMessages.text') + ')'"></span>
						<vs-input
							@keyup.13="login"
							data-vv-validate-on="blur"
							v-validate="'required|min:6|max:18'"
							:type="passwordTypeInput"
							name="password"
							v-model="password"
							class="w-full input-data"
						/>
						<feather-icon
							class="password-icon"
							:icon="iconpassword"
							svgClasses="h-6 w-6"
							@click.prevent="togglePasswordMode"
						/>
						<span class="text-danger text-sm" v-show="errors.has('password')">
							{{ $t("Login.ValidMessages.Password") }}
						</span>
					</div>
				</div>
				<div class="flex flex-wrap justify-between my-5">
					<vs-checkbox v-model="checkbox_remember_me" class="mb-3 span-remember"
						>{{ $t("general.Remember") }}
					</vs-checkbox>
				</div>
				<div class="flex flex-wrap justify-between">
					<div class="social-login-buttons flex flex-wrap items-center" v-if="!showInputPassword">
						<div
							class="bg-google pt-3 pb-2 px-4 rounded-lg cursor-pointer mr-4"
							@click="loginWithGoogle"
							:title="$t('Login.LoginWithGoogle')"
						>
							<svg
								aria-hidden="true"
								focusable="false"
								data-prefix="fab"
								data-icon="google"
								class="text-white h-4 w-4 svg-inline--fa fa-google fa-w-16"
								role="img"
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 488 512"
							>
								<path
									fill="currentColor"
									d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"
								></path>
							</svg>
						</div>
					</div>
					<vs-button
						icon-pack="feather"
						icon="icon-arrow-left-circle"
						type="border"
						class="float-left"
						v-show="showInputPassword"
						@click="handleBack"
						>{{ $t("Resource.BackButton") }}
					</vs-button>
					<vs-button
						icon-pack="feather"
						icon="icon-mail"
						:disabled="email.trim().length <= 0"
						@click="handleClick"
						>{{ loginBtnText }}
					</vs-button>
				</div>
				<hr class="ml-24 mr-24 mt-16 mb-6" style="border: 1px solid #d6d2d2" />
				<router-link to="/pages/forgot-password">
					<div class="flex justify-center items-center flex-col pt-6">
						<span class="g-span-RememberPassword" v-text="$t('general.RememberPassword')"></span>
						<span class="g-span-ForgotPassword" v-text="$t('general.ForgotPassword')"></span>
					</div>
				</router-link>
			</div>
			<div v-else>
				<form id="form" class="mb-base">
					<h4 class="mb-1">{{ $t("Login.TwoFactorAuthentication") }}</h4>
					<label>{{ $t("Login.TwoFactorAuthenticationText") }}</label>
					<div class="w-48">
						<vx-tooltip
							color="primary"
							position="bottom"
							:text="$t('Login.ResendAuthCodeEmail')"
							v-show="clockElement === 0"
						>
							<span
								class="mt-5 text-inherit underline cursor-pointer"
								color="primary"
								@click="resendCode"
								>{{ $t("Login.ResendCode") }}</span
							>
						</vx-tooltip>
					</div>
					<div class="flex flex-wrap justify-center mt-4 mb-4">
						<input
							type="tel"
							v-model="twoFactorAuthCode.code1"
							ref="code1"
							maxlength="1"
							tabindex="1"
							placeholder="·"
							autocomplete="off"
							@paste="handlePaste"
							@keyup="handleKeyup($event)"
							@input="validateInput($event)"
							id="numericInput"
							pattern="[0-9]+"
							class="m-1"
						/>

						<input
							type="tel"
							v-model="twoFactorAuthCode.code2"
							ref="code2"
							maxlength="1"
							pattern="[0-9.]+"
							tabindex="2"
							placeholder="·"
							autocomplete="off"
							class="m-1"
							@keyup="handleKeyup($event)"
							@input="validateInput($event)"
						/>
						<input
							type="tel"
							v-model="twoFactorAuthCode.code3"
							ref="code3"
							maxlength="1"
							pattern="[0-9.]+"
							tabindex="3"
							placeholder="·"
							autocomplete="off"
							class="m-1"
							@keyup="handleKeyup($event)"
							@input="validateInput($event)"
						/>
						<input
							type="tel"
							v-model="twoFactorAuthCode.code4"
							ref="code4"
							maxlength="1"
							pattern="[0-9.]+"
							tabindex="4"
							placeholder="·"
							autocomplete="off"
							class="m-1"
							@keyup="handleKeyup($event)"
							@input="validateInput($event)"
						/>
						<input
							type="tel"
							v-model="twoFactorAuthCode.code5"
							ref="code5"
							maxlength="1"
							pattern="[0-9.]+"
							tabindex="5"
							placeholder="·"
							autocomplete="off"
							class="m-1"
							@keyup="handleKeyup($event)"
							@input="validateInput($event)"
						/>
						<input
							type="tel"
							v-model="twoFactorAuthCode.code6"
							ref="code6"
							maxlength="1"
							pattern="[0-9.]+"
							tabindex="6"
							placeholder="·"
							autocomplete="off"
							@paste="handlePaste"
							@keyup="handleKeyup($event)"
							@input="validateInput($event)"
							class="m-1"
						/>
					</div>
				</form>
				<div class="flex flex-wrap justify-between mt-4">
					<vs-button
						icon-pack="feather"
						icon="icon-arrow-left-circle"
						type="border"
						@click="handleBackFromTwo"
						class="float-left"
						>{{ $t("Resource.BackButton") }}</vs-button
					>
					<vs-button
						icon-pack="feather"
						icon="icon-arrow-right-circle"
						icon-after
						:disabled="!validateTwoFactorCode"
						@click="login"
						>{{ $t("Resource.Continue") }}
					</vs-button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import User from "@/models/User.js";
import themeConfig from "../../../../themeConfig";

export default {
	props: {
		updateCompanyBrand: {
			type: Function,
		},
		resetAnimation: {
			type: Function,
		},
	},
	data() {
		return {
			email: "", //process..env.VUE_APP_FIREBASE_USER,
			password: "", //process..env.VUE_APP_FIREBASE_PASSWORD,
			checkbox_remember_me: false,
			passwordTypeInput: "password",
			iconpassword: "EyeOffIcon",
			showInputPassword: false,
			showInputCode: false,
			twoFactorAuth: false,
			twoFactorAuthCode: {
				code1: "",
				code2: "",
				code3: "",
				code4: "",
				code5: "",
				code6: "",
			},
			clockElement: 60,
			intervalCount: null,
		};
	},
	computed: {
		loginBtnText() {
			return this.showInputPassword ? this.$t("Login.Login") : this.$t("Routing.Buttons.Validate");
		},
		logingMessagesValidations() {
			return {
				InvalidPassword: this.$t("Login.ValidMessages.InvalidPassword"),
				UserNotFound: this.$t("Login.ValidMessages.UserNotFound"),
				TooManyRequests: this.$t("Login.ValidMessages.TooManyRequests"),
				CompanyReachedWorkingDate: this.$t("Login.ValidMessages.CompanyReachedWorkingDate"),
				InvalidCode: this.$t("Login.ValidMessages.InvalidCode"),
			};
		},
		validateTwoFactorCode() {
			const isBelowThreshold = (currentValue) => currentValue;
			const array1 = Object.values(this.twoFactorAuthCode);
			return array1.every(isBelowThreshold);
		},
	},
	methods: {
		togglePasswordMode() {
			this.passwordTypeInput = this.passwordTypeInput === "text" ? "password" : "text";
			this.iconpassword = this.iconpassword === "EyeOffIcon" ? "EyeIcon" : "EyeOffIcon";
		},
		login() {
			// Loading
			this.$vs.loading();
			const payload = {
				checkbox_remember_me: this.checkbox_remember_me,
				userDetails: {
					email: this.email,
					password: this.password,
				},
				notify: this.$vs.notify,
				closeAnimation: this.$vs.loading.close,
				logingMessagesValidations: this.logingMessagesValidations,
			};
			if (this.twoFactorAuth) {
				payload.code = Object.values(this.twoFactorAuthCode).join("");
			}
			this.$store
				.dispatch("auth/login", payload)
				.then((res) => {
					this.$vs.loading.close;
					return res;
				})
				.catch((err) => {
					this.$vs.loading.close;
					throw new Error(err);
				});
		},
		loginBefore() {
			const payload = {
				email: this.email,
			};

			this.$vs.loading();
			this.$store
				.dispatch("auth/loginBefore", payload)
				.then((res) => {
					if (res.data.hasOwnProperty("reason") && res.status === 400) {
						throw new Error(res.message);
					}
					const {visual_config, tfa} = res.data;
					this.twoFactorAuth = tfa ? true : false;
					if (visual_config) {
						this.updateCompanyBrand(visual_config);
						const {navbar_color, primary_color} = visual_config;
						if (navbar_color && primary_color) {
							this.$store.commit("UPDATE_PRIMARY_COLOR", navbar_color);
							this.$vs.theme({primary: primary_color, secondary: navbar_color});
						}
						this.showInputPassword = true;
					} else {
						this.$store.commit("UPDATE_PRIMARY_COLOR", themeConfig.navbarColor);
						this.$vs.theme({primary: themeConfig.navbarColor, secondary: themeConfig.navbarColor});
					}
					this.$vs.loading.close();
				})
				.catch((err) => {
					if (err.message === "The requested resource doesn't exists") {
						this.$vs.notify({
							title: this.$t("Login.Login"),
							text: this.$t("Login.ValidMessages.UserNotFound"),
							iconPack: "feather",
							icon: "icon-alert-circle",
							color: "danger",
							time: 7000,
						});
					} else {
						this.$vs.notify({
							title: this.$t("ErrorTitle"),
							text: `${err.status}: ${this.$t("ErrorMessage")} (${err.message})`,
							iconPack: "feather",
							icon: "icon-alert-circle",
							color: "danger",
							time: 7000,
						});
					}
					this.$vs.loading.close();
				});
		},
		recoveryTwoFactorCode(showAlert = false) {
			this.$vs.loading();
			const payload = {email: this.email};
			this.$store
				.dispatch("auth/recoveryTwoFactorCode", payload)
				.then((res) => {
					setTimeout(() => {
						this.showInputCode = true;
						this.$vs.loading.close();
						if (showAlert)
							this.$vs.notify({
								title: this.$t("Login.ResendCode"),
								text: this.$t("Login.CodeSent"),
								iconPack: "feather",
								icon: "icon-alert-circle",
								color: "success",
								time: 7000,
							});
					}, 1000);
					this.intervalCount = setInterval(this.updateClock, 1000);
				})
				.catch((error) => {
					this.$vs.loading.close();
					console.error(error);
				});
		},
		// Google login
		async loginWithGoogle() {
			const payload = {
				notify: this.$vs.notify,
				userDetails: {
					email: this.email,
					password: this.password,
				},
				closeAnimation: this.$vs.loading.close,
				openAnimation: this.$vs.loading,
			};
			try {
				await this.$store.dispatch("auth/loginWithGoogle", payload);
			} catch (error) {
			} finally {
			}
		},
		handleClick() {
			if (this.showInputPassword) {
				if (this.twoFactorAuth) {
					this.recoveryTwoFactorCode();
				} else {
					this.login();
				}
			} else {
				this.loginBefore();
			}
		},
		handleBack() {
			this.showInputPassword = false;
			this.resetAnimation();
			this.$vs.theme({primary: "#0b8ff4", secondary: "#0b8ff4"});
		},
		handleBackFromTwo() {
			this.showInputCode = false;
		},
		async handlePaste() {
			const clipboardData = await navigator.clipboard.readText();
			clipboardData.split("").forEach((element, index) => {
				this.twoFactorAuthCode[`code${index + 1}`] = element;
			});
		},
		handleKeyup(event) {
			const {tabIndex, value} = event.target;
			if ((event.keyCode === 8 || event.code === "Backspace") && tabIndex > 1) {
				this.focusInput(`code${tabIndex - 1}`);
				return;
			}
			if (tabIndex >= 1 && tabIndex < 6 && value !== "") this.focusInput(`code${tabIndex + 1}`);
		},
		focusInput(refName) {
			this.$refs[refName].focus();
		},
		validateInput(input) {
			const {tabIndex, value} = input.target;
			if (isNaN(value)) {
				this.twoFactorAuthCode[`code${tabIndex}`] = "";
				return;
			}
		},
		updateClock() {
			this.clockElement--;
			if (this.clockElement < 1) clearInterval(this.intervalCount);
		},
		resendCode() {
			this.clockElement = 60;
			this.resendCodeTrigger += 1;
			this.recoveryTwoFactorCode(true);
		},
	},
	mounted() {
		if (localStorage.getItem("accessToken")) {
			location.reload();
			localStorage.removeItem("accessToken");
			// Change role on logout. Same value as initialRole of acj.js
			this.$acl.change("editor");
			localStorage.removeItem("userInfo");
			const user = new User();
			user.deleteUserIndexed();
		}
		if (this.$route.query.reload === "true") {
			this.$router.replace({query: {}});
			location.reload();
		}
		const userDetail = JSON.parse(localStorage.getItem("userDetail"));
		if (userDetail) {
			this.email = userDetail.email;
			this.password = userDetail.password;
			this.checkbox_remember_me = true;
		}
		this.$vs.loading.close();
		this.$vs.theme({primary: "#0b8ff4", secondary: "#0b8ff4"});
	},
};
</script>
<style lang="scss">
#page-login {
	.social-login-buttons {
		.bg-facebook {
			background-color: #1551b1;
		}

		.bg-twitter {
			background-color: #00aaff;
		}

		.bg-google {
			background-color: #4285f4;
		}

		.bg-github {
			background-color: #333;
		}
	}
}

input[type="tel"] {
	width: 48px;
	height: 50px;
	border-bottom: 1px solid;
	border-right: none;
	border-left: none;
	border-top: none;
	font-size: xxx-large;
	font-size: -webkit-xxx-large;
	padding: 10px;
}

input[type="tel"]:focus {
	border-bottom: 3px solid gray;
}
</style>
